import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  PortalDsButton,
  PortalDsSelect,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { cn } from 'src/lib/utils';
import {
  ADMIN_SETTABLE_SUBSCRIPTION_TIERS,
  type AdminSettableSubscriptionTier,
  type PortalUserSubscriptionTier,
  type PortalWorkspaceBulkSettings,
} from '../types/users.types';
import {
  formatSubscriptionLabel,
  isPaidSubscriptionTier,
} from '../utils/portal-users.mappers';
import { QuotaOverrideEditor } from './quota-override-editor';

function getEffectiveBoolean(
  draftValue: boolean | undefined,
  currentValue: boolean,
): boolean {
  return draftValue ?? currentValue;
}

export interface TeamBulkSettingsDraft {
  creative_quota_override?: number | null;
  disable_billing_conversion?: boolean;
  hide_conversion_modal?: boolean;
  tier?: AdminSettableSubscriptionTier;
}

interface TeamBulkSettingsPanelProps {
  workspaceId: string;
  workspaceName: string;
  memberCount: number;
  currentTier: PortalUserSubscriptionTier;
  bulkSettings?: PortalWorkspaceBulkSettings | null;
  globalCreativeQuota?: number;
  disabled?: boolean;
  onApply: (
    settings: TeamBulkSettingsDraft,
  ) => Promise<{ ok: boolean; message?: string }>;
}

function hasPendingChanges(
  draft: TeamBulkSettingsDraft,
  bulkSettings: PortalWorkspaceBulkSettings | null | undefined,
  currentTier: PortalUserSubscriptionTier,
): boolean {
  if (draft.tier && draft.tier !== currentTier) {
    return true;
  }
  if (
    'creative_quota_override' in draft
    && draft.creative_quota_override !== bulkSettings?.creative_quota_override
  ) {
    return true;
  }
  if (
    'disable_billing_conversion' in draft
    && draft.disable_billing_conversion !== bulkSettings?.disable_billing_conversion
  ) {
    return true;
  }
  if (
    'hide_conversion_modal' in draft
    && draft.hide_conversion_modal !== bulkSettings?.hide_conversion_modal
  ) {
    return true;
  }
  return false;
}

function buildChangeSummary(
  draft: TeamBulkSettingsDraft,
  bulkSettings: PortalWorkspaceBulkSettings | null | undefined,
  currentTier: PortalUserSubscriptionTier,
  globalCreativeQuota: number,
): string[] {
  const changes: string[] = [];

  if (draft.tier && draft.tier !== currentTier) {
    changes.push(
      `Subscription tier → ${formatSubscriptionLabel(draft.tier)}`,
    );
  }

  if (
    'creative_quota_override' in draft
    && draft.creative_quota_override !== bulkSettings?.creative_quota_override
  ) {
    const nextValue = draft.creative_quota_override ?? globalCreativeQuota;
    changes.push(`Creative quota override → ${nextValue}`);
  }

  if (
    'disable_billing_conversion' in draft
    && draft.disable_billing_conversion !== bulkSettings?.disable_billing_conversion
  ) {
    changes.push(
      draft.disable_billing_conversion
        ? 'Exempt all members from billing limits'
        : 'Remove billing exemption for all members',
    );
  }

  if (
    'hide_conversion_modal' in draft
    && draft.hide_conversion_modal !== bulkSettings?.hide_conversion_modal
  ) {
    changes.push(
      draft.hide_conversion_modal
        ? 'Hide upgrade prompts for all members'
        : 'Show upgrade prompts for all members',
    );
  }

  return changes;
}

export function TeamBulkSettingsPanel({
  workspaceId,
  workspaceName,
  memberCount,
  currentTier,
  bulkSettings,
  globalCreativeQuota = 0,
  disabled = false,
  onApply,
}: TeamBulkSettingsPanelProps) {
  const [draft, setDraft] = useState<TeamBulkSettingsDraft>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [draftTier, setDraftTier] = useState<AdminSettableSubscriptionTier | ''>('');

  useEffect(() => {
    setDraft({});
    if (ADMIN_SETTABLE_SUBSCRIPTION_TIERS.includes(currentTier as AdminSettableSubscriptionTier)) {
      setDraftTier(currentTier as AdminSettableSubscriptionTier);
      return;
    }
    setDraftTier('');
  }, [workspaceId, currentTier, bulkSettings]);

  const effectiveBulkSettings = bulkSettings ?? {
    creative_quota_override: null,
    disable_billing_conversion: false,
    hide_conversion_modal: false,
  };

  const effectiveDisableBilling = getEffectiveBoolean(
    draft.disable_billing_conversion,
    effectiveBulkSettings.disable_billing_conversion,
  );
  const effectiveHideModal = getEffectiveBoolean(
    draft.hide_conversion_modal,
    effectiveBulkSettings.hide_conversion_modal,
  );

  const pendingDraft: TeamBulkSettingsDraft = {
    ...draft,
    ...(draftTier && draftTier !== currentTier ? { tier: draftTier } : {}),
  };

  const canApply = hasPendingChanges(pendingDraft, bulkSettings, currentTier);
  const changeSummary = buildChangeSummary(
    pendingDraft,
    bulkSettings,
    currentTier,
    globalCreativeQuota,
  );

  const handleConfirmApply = async() => {
    setInlineError(null);
    setIsApplying(true);
    const result = await onApply(pendingDraft);
    setIsApplying(false);

    if (result.ok) {
      setConfirmOpen(false);
      setDraft({});
      return;
    }

    setInlineError(result.message ?? 'Failed to apply team-wide settings.');
  };

  const isPaidTier = isPaidSubscriptionTier(currentTier);

  return (
    <div className="space-y-8">
      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Subscription tier
        </h3>
        <p className={cn(portalDs.bodySm, 'mb-3')}>
          Applies the selected tier to every member in
          {' '}
          {workspaceName}
          .
        </p>
        <div className="flex min-w-[180px] flex-col gap-1.5">
          <PortalDsSelect
            value={draftTier}
            disabled={disabled || isApplying}
            aria-label="Team subscription tier"
            onChange={(event) => {
              setDraftTier(event.target.value as AdminSettableSubscriptionTier);
            }}
            className="h-8 min-w-[180px] text-[12px]"
          >
            {ADMIN_SETTABLE_SUBSCRIPTION_TIERS.map((option) => (
              <option
                key={option}
                value={option}
              >
                {formatSubscriptionLabel(option)}
              </option>
            ))}
          </PortalDsSelect>
          <span className="text-[11px] text-[#9CA3AF]">
            {isPaidTier || isPaidSubscriptionTier(draftTier || null)
              ? 'Staged for team-wide apply. Basic/Pro may also be billed in Stripe — update Stripe separately if needed.'
              : 'Staged for team-wide apply'}
          </span>
        </div>
      </section>

      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Creative quota override
        </h3>
        <p className={cn(portalDs.bodySm, 'mb-3')}>
          Sets the same monthly creative quota override for all members.
        </p>
        <QuotaOverrideEditor
          value={effectiveBulkSettings.creative_quota_override}
          globalDefault={globalCreativeQuota}
          disabled={disabled || isApplying}
          onSave={async(value) => {
            setDraft((current) => ({ ...current, creative_quota_override: value }));
            return { ok: true };
          }}
        />
      </section>

      <section className={portalDs.sheetSection}>
        <h3 className={portalDs.sheetSectionTitle}>
          Billing
        </h3>
        <p className={cn(portalDs.bodySm, 'mb-3')}>
          Team-wide billing exemptions and upgrade prompt visibility.
        </p>
        <div className="flex flex-wrap gap-2">
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled || isApplying}
            onClick={() => {
              setDraft((current) => ({
                ...current,
                disable_billing_conversion: !effectiveDisableBilling,
              }));
            }}
          >
            {effectiveDisableBilling
              ? 'Remove billing exemption for all'
              : 'Exempt all from billing limits'}
          </PortalDsButton>
          <PortalDsButton
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled || isApplying}
            onClick={() => {
              setDraft((current) => ({
                ...current,
                hide_conversion_modal: !effectiveHideModal,
              }));
            }}
          >
            {effectiveHideModal
              ? 'Show upgrade prompts for all'
              : 'Hide upgrade prompts for all'}
          </PortalDsButton>
        </div>
      </section>

      <div className="flex flex-col gap-2">
        <PortalDsButton
          type="button"
          variant="primary"
          size="sm"
          disabled={disabled || isApplying || !canApply}
          onClick={() => setConfirmOpen(true)}
        >
          Apply team-wide settings
        </PortalDsButton>
        {inlineError ? (
          <p className={cn(portalDs.caption, 'text-[#DC2626]')}>
            {inlineError}
          </p>
        ) : null}
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(nextOpen) => {
          if (!isApplying) {
            setConfirmOpen(nextOpen);
          }
        }}
      >
        <DialogContent className="max-w-md border-[#EDEEF2] bg-white p-0 shadow-[0_4px_16px_rgba(13,15,26,0.10)]">
          <DialogHeader className="border-b border-[#EDEEF2] px-5 py-4 text-left">
            <DialogTitle className="text-[15px] font-semibold text-[#0D0F1A]">
              Apply settings to all members?
            </DialogTitle>
            <DialogDescription className="text-[13px] text-[#5E6278]">
              This will update
              {' '}
              <strong>
                {memberCount}
                {' '}
                {memberCount === 1 ? 'member' : 'members'}
              </strong>
              {' '}
              in
              {' '}
              {workspaceName}
              .
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 px-5 py-4">
            <p className="text-[13px] font-medium text-[#0D0F1A]">
              Changes to apply:
            </p>
            <ul className="list-disc space-y-1 pl-5 text-[13px] text-[#5E6278]">
              {changeSummary.map((change) => (
                <li key={change}>
                  {change}
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter className="gap-2 px-5 py-4 sm:justify-end">
            <PortalDsButton
              variant="secondary"
              size="sm"
              disabled={isApplying}
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </PortalDsButton>
            <PortalDsButton
              variant="primary"
              size="sm"
              disabled={isApplying}
              onClick={() => void handleConfirmApply()}
            >
              {isApplying ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  Applying…
                </>
              ) : (
                'Apply to all members'
              )}
            </PortalDsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
